
import { Component, Vue } from 'vue-property-decorator';
import ChatRepositoryInterface, {
  ChatInterface, chatRateMap, ChatScoreInterface,
  ChatWSMessageInterface, MessageRecipientsType,
} from '@/services/QA/chat/interfaces';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatMessageImageDialog from '@/components/dialogs/qa/chat/ChatMessageImageDialog.vue';
import EscalateDialog from '@/components/dialogs/qa/escalation/EscalateDialog.vue';
import FeedbackDialog from '@/components/dialogs/qa/feedback/FeedbackDialog.vue';
import { UserHasScopes } from '@/views/middleware/Route/Auth';
import { Roles } from '@/services/Roles';
import DeleteCommentDialog from '@/components/dialogs/qa/comment/DeleteCommentDialog.vue';
import { BaseProfileDataInterface, ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { UserBaseInfoInterface, UserRepoInterface } from '@/services/Users/Interfaces';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ChatMessageImageDialog,
    EscalateDialog,
    FeedbackDialog,
    DeleteCommentDialog,
  },
  inject: [
    'chatRepository',
    'userRepository',
    'profileRepository',
  ],
  sockets: {
    chatData(msg: ChatWSMessageInterface): void {
      // @ts-ignore
      this.mergeChatProperties(msg.chat);
    },
  },
})
export default class ViewChat extends Vue {
  private chat!: ChatInterface;
  private readonly chatRepository!: ChatRepositoryInterface;
  private readonly profileRepository!: ProfileRepositoryInterface;
  private readonly userRepository!: UserRepoInterface;

  private loading: boolean = true;
  private userHasScopes: object = UserHasScopes;
  private userRoles: object = Roles;
  private usersMap: { [userId: string]: UserBaseInfoInterface } = {};
  private chatRateMap: { '0': string, '1': string, '2': string } = chatRateMap;

  private urlForDialog: string | false = false;
  private imageDialog: string = '';
  private escalateDialog: string | false = false;
  private feedbackDialog: string | false = false;
  private chatData!: ChatInterface;
  private deletedScores: ChatScoreInterface[] = [];
  private height: number = 0;

  public async created(): Promise<void> {
    await this.initialize();
    // @ts-ignore
    this.height = this.$parent.$el.offsetHeight;
  }

  public async initialize(): Promise<void> {
    const chat = await this.chatRepository.getOneById(this.$route.params.chatId);
    if (!chat.reviewerId) {
      this.$errorDialog.show('Chat cannot be viewed, because is not reviewed yet');
      await this.$router.push('/qc/chats');
      return;
    }
    this.chat = chat;
    this.chat.scores.forEach((chatScore) => {
      if (chatScore.score.deletedAt) {
        this.deletedScores.push(chatScore);
      }
    });

    this.chat.scores = this.chat.scores.filter((chatScore) => !chatScore.score.deletedAt);

    if (this.chatData) {
      await this.mergeChatProperties(this.chatData);
    }
  }

  private escalate(): void {
    this.escalateDialog = 'escalate-dialog';
  }

  private async feedback(): Promise<void> {
    this.feedbackDialog = 'feedback-dialog';
  }

  private async closeReview(): Promise<void> {
    await this.$router.back();
  }

  private showImage(url: string): void {
    this.imageDialog = 'chat-message-image-dialog';
    this.urlForDialog = url;
  }

  private async mergeChatProperties(chatData: ChatInterface): Promise<void> {
    if (!this.chat) {
      this.chatData = chatData;
      return;
    }

    if (this.chat.id !== chatData.id) {
      delete this.chatData;
      return;
    }

    this.chat.startedTimestamp = chatData.startedTimestamp;
    this.chat.endedTimestamp = chatData.endedTimestamp;
    this.chat.visitor = chatData.visitor;
    this.chat.agents = chatData.agents;
    this.chat.supervisors = chatData.supervisors;

    this.chat.rate = chatData.rate;
    this.chat.messages.map((message) => {
      const index = chatData.messages?.findIndex((msg) => {
        return msg.id === message.id;
      });

      return Object.assign(message, chatData.messages[index]);
    });

    this.chat.messages.map((message) => {
      message.isWhisper = message.recipients === MessageRecipientsType.AGENTS;

      return message;
    });

    this.chat.messages.sort((messageA, messageB) => messageA.timestamp - messageB.timestamp);
    await this.getUsersInfo();

    const users = await this.userRepository.getUsersByEmails([
      ...this.chat.agents.map((agent) => agent.login),
      ...this.chat.supervisors.map((supervisor) => supervisor.login),
    ]);

    const usersProfiles = await this.profileRepository
      .getBaseProfileDataByUsersIds(users.map((user) => String(user.id)));

    users.forEach((user) => {
      const profileIndex = usersProfiles.findIndex((profile) => profile.userId === String(user.id));
      if (profileIndex === -1) {
        return;
      }

      const agentIndex = this.chat.agents.findIndex((agent) => agent.login === user.email);
      if (agentIndex !== -1) {
        this.chat.agents[agentIndex].id = user.id;
        this.chat.agents[agentIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }

      const supervisorIndex = this.chat.supervisors.findIndex((supervisor) => supervisor.login === user.email);
      if (supervisorIndex !== -1) {
        this.chat.supervisors[supervisorIndex].id = user.id;
        this.chat.supervisors[supervisorIndex].fullName =
          `${usersProfiles[profileIndex].firstName} ${usersProfiles[profileIndex].lastName}`;
      }
    });

    this.loading = false;
  }

  private async getUsersInfo(): Promise<void> {
    const usersIds: string[] = [];
    this.chat.comments.forEach((chatComment) => {
      if (usersIds.indexOf(chatComment.comment.authorId) === -1) {
        usersIds.push(chatComment.comment.authorId);
      }
    });

    this.chat.notes.forEach((chatNote) => {
      if (usersIds.indexOf(chatNote.note.authorId) === -1) {
        usersIds.push(chatNote.note.authorId);
      }
    });

    this.chat.messages.forEach((message) => {
      message.comments.forEach((messageComment) => {
        if (usersIds.indexOf(messageComment.comment.authorId) === -1) {
          usersIds.push(messageComment.comment.authorId);
        }
      });
    });

    this.chat.scores.forEach((chatScore) => {
      if (usersIds.indexOf(chatScore.score.authorId) === -1) {
        usersIds.push(chatScore.score.authorId);
      }
    });

    if (usersIds.indexOf(this.chat.reviewerId) === -1) {
      usersIds.push(this.chat.reviewerId);
    }

    const users = await this.userRepository.getUsersByIds(usersIds);
    users.forEach((user) => {
      this.usersMap[user.id] = user;
    });
  }
}
